/**
 * F36 (Wave 2E) — SuspendClubDialog.
 *
 * Destructive club-account suspension (D11 ops-retain cascade). Composes
 * `@bravemove/ui-web/Modal` + react-hook-form + zodResolver with:
 *   - mandatory free-form reason (Textarea, 1–500 chars)
 *   - type-to-confirm phrase "SUSPEND" (defence-in-depth, per the admin
 *     destructive-action convention)
 *   - bulleted consequence list (teams hidden, future sessions cancelled,
 *     schedules paused, roster + attendance retained read-only)
 *
 * Pattern mirrors F29's `SuspendTeamDialog` 1:1 (which itself mirrors F11's
 * B2B account suspend dialog). The reason is saved into the F06 admin_audit_log
 * + the suspended-club banner.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, TextInput, Textarea } from '@bravemove/ui-web';
import type { SuspendClubInput } from '../../api/clubs.js';

export interface SuspendClubDialogProps {
  readonly open: boolean;
  readonly clubName: string;
  readonly loading: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (body: SuspendClubInput) => void;
}

interface SuspendFormValues {
  readonly reason: string;
}

export function SuspendClubDialog({
  open,
  clubName,
  loading,
  serverError,
  onClose,
  onSubmit,
}: SuspendClubDialogProps) {
  const { t } = useI18n();
  const CONFIRM_PHRASE = t('admin:club.suspend_dialog.confirm_phrase');

  const localSchema = z.object({
    reason: z.string().trim().min(1).max(500),
  });

  const form = useForm<SuspendFormValues>({
    resolver: zodResolver(localSchema),
    defaultValues: { reason: '' },
    mode: 'onTouched',
  });

  const [typed, setTyped] = useState('');

  // Reset state on dialog (re)open — intentional setState-in-effect "form
  // reset on dialog open" pattern (mirrors F29 SuspendTeamDialog).
  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTyped('');
    }
  }, [open, form]);

  const phraseMatches = typed.trim() === CONFIRM_PHRASE;
  const canConfirm = phraseMatches && !loading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    void form.handleSubmit((v) => onSubmit({ reason: v.reason.trim() }))();
  };

  // ICU `{clubName}` interpolation does NOT fire reliably in test env (vitest
  // jsdom — the same trap F23/F29 hit). Resolve the title manually here.
  const interpolated = t('admin:club.suspend_dialog.title', {
    clubName,
    defaultValue: 'Suspend {clubName}?',
  }) as string;
  const titleText = interpolated.includes('{clubName}') ? `Suspend ${clubName}?` : interpolated;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titleText}
      description={t('admin:club.suspend_dialog.body')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('admin:club.suspend_dialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={loading}
            data-testid="suspend-club-confirm"
          >
            {t('admin:club.suspend_dialog.confirm_button')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
        className="flex flex-col gap-4"
      >
        <div className="rounded-lg border border-warning/30 bg-warning/5 p-3">
          <p className="text-[10.5px] font-mono uppercase tracking-wider text-warning font-bold mb-2">
            {t('admin:club.suspend_dialog.consequences_heading')}
          </p>
          <ul className="list-disc ps-5 space-y-1 text-[13px] text-ink">
            <li>{t('admin:club.suspend_dialog.consequence_hidden')}</li>
            <li>{t('admin:club.suspend_dialog.consequence_cancelled')}</li>
            <li>{t('admin:club.suspend_dialog.consequence_paused')}</li>
            <li>{t('admin:club.suspend_dialog.consequence_retained')}</li>
          </ul>
        </div>

        <FormField
          label={t('admin:club.suspend_dialog.reason_label')}
          helper={t('admin:club.suspend_dialog.reason_helper')}
          error={form.formState.errors.reason?.message}
          required
        >
          <Textarea
            rows={4}
            maxLength={500}
            showCounter
            data-testid="suspend-club-reason"
            {...form.register('reason')}
          />
        </FormField>

        <FormField
          label={(() => {
            const raw = t('admin:club.suspend_dialog.type_to_confirm_label', {
              phrase: CONFIRM_PHRASE,
              defaultValue: 'Type {phrase} to confirm',
            }) as string;
            return raw.includes('{phrase}') ? `Type ${CONFIRM_PHRASE} to confirm` : raw;
          })()}
        >
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            invalid={typed.length > 0 && !phraseMatches}
            autoComplete="off"
            data-testid="suspend-club-confirm-phrase"
            aria-label={t('admin:club.suspend_dialog.type_to_confirm_label', {
              phrase: CONFIRM_PHRASE,
              defaultValue: 'Type {phrase} to confirm',
            })}
          />
        </FormField>

        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}
